import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { readUrlState, hasUrlState, writeUrlState } from '../utils/urlState';
import { capCampaigns } from '../components/CampaignsFilter';
import { sanitizeVersions } from '../utils/versionFilter';
import { getPreviousMonthRange } from '../utils/datePresets';

const DashboardContext = createContext();

// Состояние из ссылки читаем один раз, до первого рендера: если в URL есть наши
// параметры — он ПОЛНОЕ описание экрана, localStorage не подмешиваем (иначе
// шаренная ссылка показала бы получателю его собственные фильтры).
const urlState = readUrlState();
const urlIsSource = hasUrlState();

// Период по умолчанию — прошлый полный календарный месяц. Применяется только
// когда нет ни диапазона в URL, ни сохранённого в localStorage. Считается при
// инициализации/сбросе, а не при загрузке модуля: вкладка может жить через границу месяца.
const getDefaultRange = () => getPreviousMonthRange();

// Ключ, который виджеты читают из localStorage напрямую
const FEES_STORAGE_KEY = 'applyPlatformFees';
const INCLUDE_ORGANIC_STORAGE_KEY = 'includeOrganic';

// Проект по умолчанию, когда нет ни URL-параметра, ни сохранённого выбора в
// localStorage. Значение — точная строка `project` из ClickHouse (см.
// server/src/services/ads/adsCommon.ts: AVZ_PROJECT).
const DEFAULT_PROJECT = 'Aliens vs Zombies: Invasion New';

// Битый JSON в localStorage не должен ронять приложение при старте
const readStoredList = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const fromUrlOr = (urlValue, readStored, fallback) => {
  if (urlIsSource) return urlValue !== undefined ? urlValue : fallback;
  return readStored();
};

// Флаг «чистого дохода» виджеты читают из localStorage сами, поэтому значение из
// ссылки надо положить туда ДО того, как виджеты смонтируются.
const initialApplyPlatformFees = fromUrlOr(
  urlState.applyPlatformFees,
  () => localStorage.getItem(FEES_STORAGE_KEY) === 'true',
  false
);
if (urlIsSource) {
  localStorage.setItem(FEES_STORAGE_KEY, initialApplyPlatformFees ? 'true' : 'false');
}

// Дефолт обратный «чистому доходу»: отсутствие значения в localStorage должно
// означать true (включать органику), а не false — поэтому сравниваем со
// строкой 'false', а не 'true'.
const initialIncludeOrganic = fromUrlOr(
  urlState.includeOrganic,
  () => localStorage.getItem(INCLUDE_ORGANIC_STORAGE_KEY) !== 'false',
  true
);

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  // Активный раздел
  const [activeSection, setActiveSectionState] = useState(() => fromUrlOr(
    urlState.section,
    () => {
      const saved = localStorage.getItem('dashboardActiveSection');
      // Миграция старых значений на новые
      if (saved === 'campaigns' || saved === 'registrations') {
        return 'users';
      }
      return saved || 'users';
    },
    'users'
  ));

  // Активная вкладка по разделам: { [section]: tabId }. Хранится id вкладки, а не
  // индекс — ссылка остаётся валидной при переупорядочивании вкладок.
  const [activeTabs, setActiveTabs] = useState(() => (
    urlIsSource && urlState.tab ? { [urlState.section || 'users']: urlState.tab } : {}
  ));

  // Обертка для setActiveSection чтобы сохранять в localStorage
  const setActiveSection = useCallback((section) => {
    setActiveSectionState(section);
    localStorage.setItem('dashboardActiveSection', section);
  }, []);

  // Установка активной вкладки раздела (id вкладки)
  const setActiveTabForSection = useCallback((section, tabId) => {
    setActiveTabs(prev => (prev[section] === tabId ? prev : { ...prev, [section]: tabId }));
    localStorage.setItem(`${section}-active-tab`, JSON.stringify(tabId));
  }, []);

  // Даты фильтрации
  const [startDate, setStartDate] = useState(() => fromUrlOr(
    urlState.startDate,
    () => {
      const saved = localStorage.getItem('dashboardStartDate');
      return saved ? new Date(saved) : getDefaultRange().start;
    },
    getDefaultRange().start
  ));

  const [endDate, setEndDate] = useState(() => fromUrlOr(
    urlState.endDate,
    () => {
      const saved = localStorage.getItem('dashboardEndDate');
      return saved ? new Date(saved) : getDefaultRange().end;
    },
    getDefaultRange().end
  ));

  // Время фильтрации
  const [startTime, setStartTime] = useState(() => fromUrlOr(
    urlState.startTime,
    () => {
      const saved = localStorage.getItem('dashboardStartTime');
      return saved ? JSON.parse(saved) : { hours: 0, minutes: 0 };
    },
    { hours: 0, minutes: 0 }
  ));

  const [endTime, setEndTime] = useState(() => fromUrlOr(
    urlState.endTime,
    () => {
      const saved = localStorage.getItem('dashboardEndTime');
      return saved ? JSON.parse(saved) : { hours: 23, minutes: 59 };
    },
    { hours: 23, minutes: 59 }
  ));

  // Выбранный проект
  const [selectedProject, setSelectedProject] = useState(() => fromUrlOr(
    urlState.project,
    () => {
      const saved = localStorage.getItem('dashboardSelectedProject');
      return saved ? JSON.parse(saved) : DEFAULT_PROJECT;
    },
    DEFAULT_PROJECT
  ));

  // Фильтр кампаний
  const [selectedCampaigns, setSelectedCampaigns] = useState(() => capCampaigns(fromUrlOr(
    urlState.campaigns,
    () => {
      const saved = localStorage.getItem('dashboardSelectedCampaigns');
      return saved ? JSON.parse(saved) : [];
    },
    []
  )));

  // Фильтр источника трафика
  const [selectedMediaSource, setSelectedMediaSource] = useState(() => fromUrlOr(
    urlState.mediaSource,
    () => localStorage.getItem('dashboardSelectedMediaSource') || 'all',
    'all'
  ));

  // Фильтр платформы
  const [selectedPlatform, setSelectedPlatform] = useState(() => fromUrlOr(
    urlState.platform,
    () => localStorage.getItem('dashboardSelectedPlatform') || 'all',
    'all'
  ));

  // Фильтры версий клиента (client_ver) и установки (client_ver_i)
  const [selectedClientVersions, setSelectedClientVersions] = useState(() => sanitizeVersions(fromUrlOr(
    urlState.clientVersions,
    () => readStoredList('dashboardSelectedClientVersions'),
    []
  )));

  const [selectedInstallVersions, setSelectedInstallVersions] = useState(() => sanitizeVersions(fromUrlOr(
    urlState.installVersions,
    () => readStoredList('dashboardSelectedInstallVersions'),
    []
  )));

  // Применять ли коэффициенты платформ к inapps revenue
  const [applyPlatformFees, setApplyPlatformFees] = useState(initialApplyPlatformFees);

  // Учитывать ли органику (source = 'Organic') в разделе ROAS. Общий для всех
  // трёх вкладок ROAS, по умолчанию включён.
  const [includeOrganic, setIncludeOrganic] = useState(initialIncludeOrganic);

  // Кэш загруженных данных
  const [sectionData, setSectionData] = useState({
    campaigns: null,
    creatives: null,
    alerts: null,
    dashboard: null
  });

  // Состояния загрузки для каждого раздела
  const [loading, setLoading] = useState({
    campaigns: false,
    creatives: false,
    alerts: false,
    dashboard: false
  });

  // Ошибки для каждого раздела
  const [errors, setErrors] = useState({
    campaigns: null,
    creatives: null,
    alerts: null,
    dashboard: null
  });

  // Обновление дат с сохранением в localStorage
  const updateDates = useCallback((newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);

    if (newStartDate) {
      localStorage.setItem('dashboardStartDate', newStartDate.toISOString());
    } else {
      localStorage.removeItem('dashboardStartDate');
    }

    if (newEndDate) {
      localStorage.setItem('dashboardEndDate', newEndDate.toISOString());
    } else {
      localStorage.removeItem('dashboardEndDate');
    }

    // Сбрасываем кэш при изменении дат
    setSectionData({
      campaigns: null,
      creatives: null,
      alerts: null,
      dashboard: null
    });
  }, []);

  // Обновление времени с сохранением в localStorage
  const updateTimes = useCallback((newStartTime, newEndTime) => {
    if (newStartTime) {
      setStartTime(newStartTime);
      localStorage.setItem('dashboardStartTime', JSON.stringify(newStartTime));
    }

    if (newEndTime) {
      setEndTime(newEndTime);
      localStorage.setItem('dashboardEndTime', JSON.stringify(newEndTime));
    }

    // Сбрасываем кэш при изменении времени
    setSectionData({
      campaigns: null,
      creatives: null,
      alerts: null,
      dashboard: null
    });
  }, []);

  // Сброс дат и времени
  const resetDates = useCallback(() => {
    const range = getPreviousMonthRange();
    updateDates(range.start, range.end);
    setStartTime({ hours: 0, minutes: 0 });
    setEndTime({ hours: 23, minutes: 59 });
    localStorage.removeItem('dashboardStartTime');
    localStorage.removeItem('dashboardEndTime');
  }, [updateDates]);

  // Обновление выбранного проекта
  const updateSelectedProject = useCallback((project) => {
    setSelectedProject(project);
    if (project) {
      localStorage.setItem('dashboardSelectedProject', JSON.stringify(project));
    } else {
      localStorage.removeItem('dashboardSelectedProject');
    }

    // Сбрасываем кэш при изменении проекта
    setSectionData({
      campaigns: null,
      creatives: null,
      alerts: null,
      dashboard: null
    });
  }, []);

  // Обновление выбранных кампаний
  const updateSelectedCampaigns = useCallback((campaigns) => {
    const cappedCampaigns = capCampaigns(campaigns);
    setSelectedCampaigns(cappedCampaigns);
    localStorage.setItem('dashboardSelectedCampaigns', JSON.stringify(cappedCampaigns));

    // Сбрасываем кэш при изменении кампаний
    setSectionData({
      campaigns: null,
      creatives: null,
      alerts: null,
      dashboard: null
    });
  }, []);

  // Обновление выбранного источника трафика
  const updateSelectedMediaSource = useCallback((mediaSource) => {
    setSelectedMediaSource(mediaSource);
    localStorage.setItem('dashboardSelectedMediaSource', mediaSource);

    // Сбрасываем кэш при изменении источника
    setSectionData({
      campaigns: null,
      creatives: null,
      alerts: null,
      dashboard: null
    });
  }, []);

  // Обновление выбранной платформы
  const updateSelectedPlatform = useCallback((platform) => {
    setSelectedPlatform(platform);
    localStorage.setItem('dashboardSelectedPlatform', platform);

    // Сбрасываем кэш при изменении платформы
    setSectionData({
      campaigns: null,
      creatives: null,
      alerts: null,
      dashboard: null
    });
  }, []);

  // Обновление версий клиента и установки
  const updateSelectedVersions = useCallback((clientVersions, installVersions) => {
    const client = sanitizeVersions(clientVersions);
    const install = sanitizeVersions(installVersions);
    setSelectedClientVersions(client);
    setSelectedInstallVersions(install);
    localStorage.setItem('dashboardSelectedClientVersions', JSON.stringify(client));
    localStorage.setItem('dashboardSelectedInstallVersions', JSON.stringify(install));

    // Сбрасываем кэш при изменении версий
    setSectionData({
      campaigns: null,
      creatives: null,
      alerts: null,
      dashboard: null
    });
  }, []);

  // Обновление флага применения коэффициентов платформ.
  // Единственный писатель ключа applyPlatformFees: виджеты читают его из
  // localStorage и слушают событие platformFeesChanged.
  const updateApplyPlatformFees = useCallback((apply) => {
    setApplyPlatformFees(apply);
    localStorage.setItem(FEES_STORAGE_KEY, apply ? 'true' : 'false');
    window.dispatchEvent(new Event('platformFeesChanged'));

    // НЕ сбрасываем кэш - это только клиентская обработка данных
  }, []);

  // Обновление флага учёта органики в ROAS. В отличие от applyPlatformFees
  // требует нового запроса к серверу — виджеты подписаны на него через
  // useWidgetData(fetchFn, [includeOrganic]), сброс кэша не нужен.
  const updateIncludeOrganic = useCallback((include) => {
    setIncludeOrganic(include);
    localStorage.setItem(INCLUDE_ORGANIC_STORAGE_KEY, include ? 'true' : 'false');
  }, []);

  // Установка данных для раздела
  const setSectionDataForSection = useCallback((section, data) => {
    setSectionData(prev => ({
      ...prev,
      [section]: data
    }));
  }, []);

  // Установка состояния загрузки для раздела
  const setLoadingForSection = useCallback((section, isLoading) => {
    setLoading(prev => ({
      ...prev,
      [section]: isLoading
    }));
  }, []);

  // Установка ошибки для раздела
  const setErrorForSection = useCallback((section, error) => {
    setErrors(prev => ({
      ...prev,
      [section]: error
    }));
  }, []);

  // Держим адресную строку в актуальном состоянии — её можно копировать в любой момент
  useEffect(() => {
    writeUrlState({
      section: activeSection,
      tab: activeTabs[activeSection],
      startDate,
      endDate,
      startTime,
      endTime,
      project: selectedProject,
      mediaSource: selectedMediaSource,
      campaigns: selectedCampaigns,
      platform: selectedPlatform,
      clientVersions: selectedClientVersions,
      installVersions: selectedInstallVersions,
      applyPlatformFees,
      includeOrganic
    });
  }, [
    activeSection,
    activeTabs,
    startDate,
    endDate,
    startTime,
    endTime,
    selectedProject,
    selectedMediaSource,
    selectedCampaigns,
    selectedPlatform,
    selectedClientVersions,
    selectedInstallVersions,
    applyPlatformFees,
    includeOrganic
  ]);

  const value = {
    // Состояния
    activeSection,
    activeTabs,
    startDate,
    endDate,
    startTime,
    endTime,
    dateRange: { startDate, endDate, startTime, endTime },
    selectedProject,
    selectedCampaigns,
    selectedMediaSource,
    selectedPlatform,
    selectedClientVersions,
    selectedInstallVersions,
    applyPlatformFees,
    includeOrganic,
    sectionData,
    loading,
    errors,

    // Методы
    setActiveSection,
    setActiveTabForSection,
    updateDates,
    updateTimes,
    resetDates,
    updateSelectedProject,
    updateSelectedCampaigns,
    updateSelectedMediaSource,
    updateSelectedPlatform,
    updateSelectedVersions,
    updateApplyPlatformFees,
    updateIncludeOrganic,
    setSectionDataForSection,
    setLoadingForSection,
    setErrorForSection
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};
